import { useEffect } from 'react'
import { interactiveFramePublisher } from './interactive-frame'

/**
 * Drives the window's interactive state from a single cursor hit-test against
 * the registered bounds — the same predicate the debugger and the main-process
 * poll use.
 *
 * This replaces per-element pointer enter/leave on the `display: contents`
 * `<Interactive>` wrappers. Those keyed off the union of child boxes rather than
 * the registered (positioned-ancestor) box, so the cursor flickered to
 * click-through over gaps/seams inside a card and never engaged at all for
 * pointer-events-none content like toasts. Hit-testing the registered bounds
 * makes "interactive" mean exactly what the debugger draws.
 *
 * The overlay receives forwarded mousemove even while click-through
 * (`setIgnoreMouseEvents(true, { forward: true })`), so this stays live in both
 * states. The main-process poll remains the backstop for the one case mousemove
 * can't cover: a stationary cursor with bounds animating underneath it.
 */
export function useInteractiveHitTest(): void {
  useEffect(() => {
    const onMove = (e: MouseEvent | PointerEvent) => {
      interactiveFramePublisher.publishPointer(e.clientX, e.clientY)
    }
    document.addEventListener('mousemove', onMove, true)
    document.addEventListener('pointermove', onMove, true)
    return () => {
      document.removeEventListener('mousemove', onMove, true)
      document.removeEventListener('pointermove', onMove, true)
    }
  }, [])
}
